import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import moment from "moment";
import { fetchDocumentMergeFields, updateDocumentMergeFields } from "../store/actions/documentActions";
import { fetchZohoCrmFieldsSchema } from "../store/actions/companyActions";
import { EditorContext } from "../containers/editor/EditorLayout";
import {
  CUSTOM,
  OTHERS,
  LOCAL_KEY_DOCUMENT_SAVE_COMPLETED,
  SESSION_KEY_CONTENT_EDITABLE_LAST_SELECTION,
  SESSION_KEY_MF_INFO_MODAL_DATA,
  SYSTEM,
  TEXT,
  TYPE_PROPOSAL,
  SESSION_KEY_TARGET_CMF_TEXT_WIDGET_IDS,
  LOCAL_KEY_DOCUMENT_NAME,
  SESSION_KEY_DOCUMENT_SOURCE,
} from "../constants/editor";
import {
  calculateNewPositionOnRotatedObjectResize,
  getCssTransformObj,
  getRandomString,
  getStringToValidJSON,
  getUpdatedGroupInnerWidgetPos,
  getWidgetAndParentsDomReference,
  parseStrToDomNode,
  removeDuplicatesFromArrayOfObject,
  removeDuplicatesFromPlainArray,
  removeWhiteSpaceBetweenTags,
  setGMTAtSession,
  toProperCase,
} from "../_helpers/utils";
import { useSocket } from "./useSocket";
import { SYNC_MERGE_FIELD } from "../constants/socket";
import { GET_CONNECTED_ACCOUNTS_SUCCESS } from "../store/actions/actionTypes";
import useModuleName from "./useModuleName";

const EDITABLE_SELECTOR = '.dhp-content-editable-true-text [contenteditable="true"]';

const useMergeFields = () => {
  const webSocket = useSocket();
  const dispatch = useDispatch();
  const { id: documentId } = useParams();
  const isDocCreatingFromLibrary = sessionStorage.getItem(SESSION_KEY_DOCUMENT_SOURCE) === "library";
  const {
    metadata,
    widgets,
    updateWidgets,
    documentType,
    isDocumentReady,
    mfSchemaDataRef,
    missingMfDataRef,
    setShowMfBulkUpdateModal,
    setShowMergeFieldSelectorModal,
    setShowMergeFieldInfoModal,
    mergeFieldInfoModalData,
    setMergeFieldInfoModalData,
  } = useContext(EditorContext);

  const userInfo = useSelector(state => state?.auth?.user);
  const { mfSchemaData, mfSchemaDataFirstFetch, changeBySocket, documentDetails } = useSelector(
    state => state.document
  );
  const currentCompanyId = useSelector(state => state.auth?.user?.company?.id);
  const countries = useSelector(state => state.resources?.countries);
  const actionType = useSelector(state => state.company?.type);

  const getName = useModuleName({ provider: documentDetails?.data?.data_source?.provider });

  const [schemaFetching, setSchemaFetching] = useState(false);

  const isDocumentSigned = ["declined", "partially_signed", "signed"].includes(documentDetails?.data?.proposal?.status);

  const fetchSchemaData = async () => {
    setSchemaFetching(true);
    const resp = await dispatch(fetchDocumentMergeFields({ documentId }));
    const schemaData = resp?.result?.success ? resp?.result?.merge_fields : {};
    const formattedSchemaData = await _helpers.getFormattedSchemaData(schemaData);
    mfSchemaDataRef.current = formattedSchemaData;
    setTimeout(() => setSchemaFetching(false), 3000);
  };

  const insertMergeField = props => {
    _helpers.restoreSelection();

    setTimeout(() => {
      const { mergeFieldNode } = _helpers.generateMergeFieldNode(props);
      _helpers.insertRichTextAtHash(mergeFieldNode);
      _helpers.generateMissingMfData();
    }, 100);
  };

  const deleteMergeField = e => {
    const { editable } = _helpers.getEditableNode();
    if (!editable || document.activeElement !== editable) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0).cloneRange();
    const { caretPos = null, mfNode = null } = _helpers.getTargetMergeFieldFromSelection() ?? {};

    // 1. Handle Backspace (delete if caret is immediately after mf, or mf is selected)
    if (e.key === "Backspace" && ["selected", "after"].includes(caretPos)) {
      e.preventDefault();
      _helpers.deleteMergeFieldAndKeepCaret(mfNode.id);
      return;
    }

    // 2. Handle Delete (delete if caret is immediately before mf, or mf is selected)
    else if (e.key === "Delete" && ["selected", "before"].includes(caretPos)) {
      e.preventDefault();
      _helpers.deleteMergeFieldAndKeepCaret(mfNode.id);
      return;
    }

    // 3. Restrict rest of the use cases (like pressing any key with full selection, exceptions: arrow keys and utility combo keys, note: backspace and delete are already bypassed in above conditions)
    else if (!range.collapsed && !_helpers.isExceptionKeyPressed({ e })) {
      const contents = range.cloneContents();
      const children = Array.from(contents.childNodes);

      // Helper: Check recursively if all nodes are merge fields
      const allAreMergeFields = nodes => {
        return nodes.every(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.classList.contains("merge-field")) return true;

            // For wrapping elements like <div>, <span>, drill down
            return allAreMergeFields(Array.from(node.childNodes));
          }
          // Text nodes not allowed unless inside merge field
          return false;
        });
      };

      // Selection includes only merge field (single)
      if (children.length > 0 && allAreMergeFields(children)) {
        e.preventDefault();
        return;
      }
    }

    // finally close info modal (if applicable)
    setShowMergeFieldInfoModal?.(false);
  };

  const controlMergeFieldSelection = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const { startContainer, endContainer } = range;

    const startField = _helpers.isInMergeField(startContainer);
    const endField = _helpers.isInMergeField(endContainer);

    if (startField && endField && startField === endField) {
      // Partial selection inside the same merge field — force full select
      const newRange = document.createRange();
      newRange.selectNodeContents(startField);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  };

  const toggleMergeFieldSelectorModal = () => {
    if (!setShowMergeFieldSelectorModal) return;

    const { editable } = _helpers.getEditableNode();
    if (!editable || document.activeElement !== editable) return;

    if (_helpers.isTextSelected()) {
      setShowMergeFieldSelectorModal(false);
      return;
    }

    const { isHashAtCursor } = _helpers.isHashAtCursor();
    if (isHashAtCursor) {
      _helpers.saveSelection();
      setShowMergeFieldSelectorModal(true);
    } else {
      setShowMergeFieldSelectorModal(false);
    }
  };

  const focusOutContentEditable = () => {
    setTimeout(() => {
      const { editable } = _helpers.getEditableNode();
      if (!editable) {
        _helpers.generateMissingMfData();
        setShowMergeFieldInfoModal?.(false);
        setShowMergeFieldSelectorModal?.(false);
      }
    }, 100);
  };

  const handleKeyUp = () => {
    expandSelectionToMergeFields();
    toggleMergeFieldSelectorModal();
  };

  const handleSelectionChange = () => {
    controlMergeFieldSelection();
    toggleMergeFieldSelectorModal();
  };

  const handleMouseDown = e => {
    // stop updating document in-case signed document (fully/partially accepted)
    if ([TYPE_PROPOSAL].includes(documentType) && isDocumentSigned) return;

    focusOutContentEditable();
    setTimeout(() => {
      // delay for controlMergeFieldSelection() func to execute
      toggleMergeFieldInfoModal(e);
    }, 100);
  };

  const handleScroll = () => {
    const { editable } = _helpers.getEditableNode();
    if (!editable) {
      // hide operative mf modals on scroll: provided editable node is not found
      setShowMergeFieldInfoModal?.(false);
      setShowMergeFieldSelectorModal?.(false);
    }
  };

  const toggleMergeFieldInfoModal = e => {
    if (!(setShowMergeFieldInfoModal && setMergeFieldInfoModalData)) return;

    const { editable } = _helpers.getEditableNode();
    const isOnMergeField = e.target.closest(".merge-field");

    if (editable && isOnMergeField) {
      // double protection: check if only single merge field is selected
      const { caretPos = null } = _helpers.getSelectedMergeFieldNode() ?? {};
      if (caretPos !== "selected") return;

      // open modal
      const dataObj = { id: isOnMergeField.id };
      const datasetObj = { ...isOnMergeField.dataset };
      Object.keys(datasetObj).forEach(key => (dataObj[key.replace("mf", "").toLowerCase()] = datasetObj[key]));

      setMergeFieldInfoModalData({ ...dataObj, uiData: dataObj.data });
      setShowMergeFieldInfoModal(true);

      _helpers.saveSelection();
    } else {
      if (
        !(
          (e.target.closest(".dhp-navAside") || e.target.closest(".dhp-secondAside")) &&
          document.querySelector(".merge-field-asset-pane")
        )
      ) {
        // PREVENT if clicked on {{ mf-info-modal }} from left asset pane DURING MF OPERTAIONS
        setShowMergeFieldInfoModal(false);
      }
    }
  };

  const deleteMergeFieldFromInfoModal = () => {
    _helpers.restoreSelection();

    _helpers.deleteMergeFieldAndKeepCaret(mergeFieldInfoModalData.id);
    setShowMergeFieldInfoModal(false);

    // remove session storage
    sessionStorage.removeItem(SESSION_KEY_MF_INFO_MODAL_DATA);
  };

  const updateMergeFieldFromInfoModal = () => {
    if (!setShowMergeFieldInfoModal) return;
    const {
      id: mfId = null,
      type: mfType = null,
      data: mfData = null,
      label: mfLabel = null,
      inValid: mfInValid = null,
      isEmpty: mfIsEmpty = null,
      module: mfModule = null,
      field: mfField = null,
    } = (mergeFieldInfoModalData?.id
      ? mergeFieldInfoModalData
      : getStringToValidJSON(sessionStorage.getItem(SESSION_KEY_MF_INFO_MODAL_DATA))) ?? {};

    if (mfType === CUSTOM.SLUG) {
      // applicable only for custom merge fields

      if (!mfInValid) {
        const targetWidgetIds = [];

        // update entire document with updated value for this custom mf
        const mergeKey = mfModule ? `${mfType}.${mfModule}.${mfField}` : `${mfType}.${mfField}`;
        const allCustomMfs = _helpers.getUniqueMergeFields({ selector: `[data-mf-key="${mergeKey}"]` });

        allCustomMfs.forEach(cmf => {
          cmf.innerText = mfIsEmpty ? mfLabel : mfData;
          cmf.dataset.mfData = mfIsEmpty ? "NULL" : mfData;
          cmf.classList.toggle("empty", mfIsEmpty);
          cmf.classList.toggle("filled", !mfIsEmpty);

          // store widget ids
          const widgetId = _helpers.getWidgetIdByMfNode({ mfNode: cmf });
          if (!targetWidgetIds.includes(widgetId)) targetWidgetIds.push(widgetId);
        });

        // store targetWidgetIds is session storage to update them from useTextFocusOut hook (while performing document save after text widget focus out)
        if (targetWidgetIds.length > 0) {
          sessionStorage.setItem(SESSION_KEY_TARGET_CMF_TEXT_WIDGET_IDS, JSON.stringify(targetWidgetIds));

          // update handler after cmf updation
          _helpers.updateWidgetHandlerHeight();
        }
      }
    }

    // close modal and remove session data after all changes done
    setShowMergeFieldInfoModal(false);
    sessionStorage.removeItem(SESSION_KEY_MF_INFO_MODAL_DATA);
  };

  const bulkUpdateCustomMergeFields = payload => {
    const targetWidgetIds = [];

    Object.keys(payload).forEach(key => {
      const cmfNodes = _helpers.getUniqueMergeFields({ selector: `[data-mf-key="${key}"]` });
      const mfData = payload[key];
      cmfNodes.forEach(cmf => {
        cmf.innerText = mfData;
        cmf.dataset.mfData = mfData;
        cmf.classList.remove("empty");
        cmf.classList.add("filled");

        // store widget ids
        const widgetId = _helpers.getWidgetIdByMfNode({ mfNode: cmf });
        if (!targetWidgetIds.includes(widgetId)) targetWidgetIds.push(widgetId);
      });
    });

    // update context to trigger document save
    _helpers.updateReactDom({ targetWidgetIds });

    // close modal && session data after all changes done
    setShowMfBulkUpdateModal(false);
    sessionStorage.removeItem(SESSION_KEY_DOCUMENT_SOURCE);
  };

  const expandSelectionToMergeFields = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    let startNode = range.startContainer;
    let endNode = range.endContainer;

    // If selection is within a text node, get the parent element
    if (startNode.nodeType === 3) startNode = startNode.parentNode;
    if (endNode.nodeType === 3) endNode = endNode.parentNode;

    // Traverse up to span.merge-field if any part is inside it
    const startMF = startNode.closest(".merge-field");
    const endMF = endNode.closest(".merge-field");

    if (startMF || endMF) {
      // Partial selection of merge fields and normal text — force full select the merge field (works for both start and end)
      const newRange = document.createRange();

      // If startMF found, set start before the merge field
      if (startMF) newRange.setStartBefore(startMF);
      else newRange.setStart(range.startContainer, range.startOffset);

      // If endMF found, set end after the merge field
      if (endMF) newRange.setEndAfter(endMF);
      else newRange.setEnd(range.endContainer, range.endOffset);

      selection.removeAllRanges();
      selection.addRange(newRange);
    }
  };

  const restrictDraggingMergeFields = e => {
    const { editable } = _helpers.getEditableNode();
    if (!editable) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const fragment = range.cloneContents();

    // Check if any merge-field is part of the selected content (text + mfs)
    const isMfsInSelection = fragment.querySelector(".merge-field");
    if (isMfsInSelection) e.preventDefault();
  };

  const restrictDropInsideMergeField = e => {
    const { editable } = _helpers.getEditableNode();
    if (!editable) return;

    const dropTarget = document.elementFromPoint(e.clientX, e.clientY);
    const isOverMf = dropTarget.closest(".merge-field");
    if (isOverMf) e.preventDefault();
  };

  const updateMfCountInDb = () => {
    // this update is associated with document details and list api
    const allSmfs = [...document.querySelectorAll(`.merge-field[data-mf-type="${SYSTEM.SLUG}"]`)];
    const allOmfs = [...document.querySelectorAll(`.merge-field[data-mf-type="${OTHERS.SLUG}"]`)];
    const allCmfs = [...document.querySelectorAll(`.merge-field[data-mf-type="${CUSTOM.SLUG}"]`)];
    const allCmfIds = allCmfs?.filter(cmf => cmf.dataset.mfOid)?.map(cmf => cmf.dataset.mfOid);
    const filteredCmfIds = allCmfIds.length > 0 ? removeDuplicatesFromPlainArray(allCmfIds) : [];
    const payload = {
      [SYSTEM.SLUG]: allSmfs.length > 0,
      [OTHERS.SLUG]: allOmfs.length > 0,
      [CUSTOM.SLUG]: filteredCmfIds,
    };

    dispatch(updateDocumentMergeFields(documentId, payload)).then(resp => {
      if (resp.status === 200 && filteredCmfIds.length > 0 && webSocket?.readyState === 1) {
        // trigger socket (collaboration for company settings > custom merge fields)
        const subscribe = {
          type: SYNC_MERGE_FIELD,
          companyId: currentCompanyId,
          updateMfCountInDb: true,
        };
        webSocket.send(JSON.stringify(subscribe));
      }
    });
  };

  const firstFetchPostActions = async () => {
    // stop updating document in-case signed document (fully/partially accepted)
    if ([TYPE_PROPOSAL].includes(documentType) && isDocumentSigned) return;

    // for first time fetch of mfSchemaData
    const formattedSchemaData = await _helpers.getFormattedSchemaData(mfSchemaData);
    mfSchemaDataRef.current = formattedSchemaData;

    const isMfPresent = [...document.querySelectorAll(".merge-field")]?.length > 0;
    if (isMfPresent) _helpers.postSchemaDataFetchActions(formattedSchemaData);
  };

  const documentSavePostActions = () => {
    // stop updating document in-case signed document (fully/partially accepted)
    if ([TYPE_PROPOSAL].includes(documentType) && isDocumentSigned) return;

    // re-calculate missing mfs on context data update for widgets (covers all usecases like duplicate/copy/paste/delete/undo/redo)
    focusOutContentEditable();

    // update system merge field count in db
    updateMfCountInDb();
  };

  const _helpers = {
    getEditableNode: () => {
      const editable = document.querySelector(EDITABLE_SELECTOR);
      const editableParent = editable?.closest(".dhp-content-editable-true-text");
      return editableParent ? { editable, editableParent } : { editable: null, editableParent: null };
    },

    isInMergeField: node => {
      if (!node) return null;
      return node.nodeType === 3 ? node.parentElement.closest(".merge-field") : node.closest(".merge-field");
    },

    getTargetMergeFieldFromSelection: () => {
      let result;
      const selection = window.getSelection();
      if (!selection.rangeCount) return null;

      const range = selection.getRangeAt(0);

      // CASE 1: Caret is collapsed (not selection)
      if (range.collapsed) {
        result = _helpers.getCaretAdjacentMergeField();
      }

      // CASE 2: Caret is a selection (not collapsed)
      else {
        result = _helpers.getSelectedMergeFieldNode();
      }

      return result;
    },

    getSelectedMergeFieldNode: () => {
      const selection = window.getSelection();
      if (!selection.rangeCount) return null;

      const range = selection.getRangeAt(0);
      const content = range.cloneContents();
      const selectedNode = content.querySelector(".merge-field");

      // Check if full merge field is selected (optional)
      if (selectedNode && selectedNode.textContent === range.toString().trim()) {
        return { caretPos: "selected", mfNode: selectedNode };
      }

      return null;
    },

    getCaretAdjacentMergeField: () => {
      const selection = window.getSelection();
      if (!selection.rangeCount || !selection.isCollapsed) return null;

      const range = selection.getRangeAt(0);
      const { startContainer, startOffset } = range;

      // Case 1: Caret is directly before a merge field
      if (startContainer.nodeType === Node.ELEMENT_NODE) {
        const childAtCaret = startContainer.childNodes[startOffset];
        if (childAtCaret?.classList?.contains("merge-field")) {
          return { caretPos: "before", mfNode: childAtCaret };
        }

        const prevSibling = startContainer.childNodes[startOffset - 1];
        if (prevSibling?.classList?.contains("merge-field")) {
          return { caretPos: "after", mfNode: prevSibling };
        }
      }

      // Case 2: Caret is inside a text node (e.g., in between text and merge field)
      if (startContainer.nodeType === Node.TEXT_NODE) {
        const parent = startContainer.parentNode;
        const siblings = Array.from(parent.childNodes);
        const index = siblings.indexOf(startContainer);

        // Only when at start or end of text
        if (startOffset === 0) {
          const before = siblings[index - 1];
          if (before?.classList?.contains("merge-field")) {
            return { caretPos: "after", mfNode: before };
          }
        }

        if (startOffset === startContainer.textContent.length) {
          const after = siblings[index + 1];
          if (after?.classList?.contains("merge-field")) {
            return { caretPos: "before", mfNode: after };
          }
        }
      }

      return null;
    },

    deleteMergeFieldAndKeepCaret: mergeFieldId => {
      const { editable } = _helpers.getEditableNode();
      if (!editable || document.activeElement !== editable) return;

      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      // queryselector on editable re-establish DOM reference to the merge field (otherwise deleting from info modal is not working)
      const mergeField = editable.querySelector(`#${mergeFieldId}`);
      if (!mergeField) return;

      const range = selection.getRangeAt(0).cloneRange();

      // Move the range before the merge field
      range.setStartBefore(mergeField);
      range.setEndBefore(mergeField);

      // Collapse the range so it's a caret (not a selection)
      range.collapse(true);

      // Delete the merge field
      mergeField.remove();

      // Focus the editable div
      editable.focus();

      // Restore caret
      selection.removeAllRanges();
      selection.addRange(range);

      // update handler after mf deletion
      _helpers.updateWidgetHandlerHeight();
    },

    isHashAtCursor: () => {
      const { editable } = _helpers.getEditableNode();
      if (!editable || document.activeElement !== editable) return;

      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0).cloneRange();
      const caretOffset = range.startOffset;

      const text = range.startContainer.textContent;
      const beforeCursor = text.slice(0, caretOffset);
      const match = beforeCursor.match(/\s*#+$/);
      const isHashAtCursor = !!match;

      return { isHashAtCursor };
    },

    isExceptionKeyPressed: ({ e, exceptions = [] }) => {
      return (
        [...exceptions, "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Shift", "Alt", "Control", "Meta"].includes(
          e.key
        ) ||
        (e.shiftKey && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) ||
        ((e.ctrlKey || e.metaKey) && ["b", "i", "u"].includes(e.key.toLowerCase()))
      );
    },

    isTextSelected: () => {
      const selection = window.getSelection();
      return selection && selection.type === "Range" && selection.toString().length > 0;
    },

    saveSelection: () => {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const getNodePath = (node, root = document.body) => {
        const path = [];
        while (node && node !== root) {
          const parent = node.parentNode;
          if (!parent) break;
          const index = Array.from(parent.childNodes).indexOf(node);
          path.unshift(index);
          node = parent;
        }
        return path;
      };

      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const serializedRange = {
          startPath: getNodePath(range.startContainer),
          startOffset: range.startOffset,
          endPath: getNodePath(range.endContainer),
          endOffset: range.endOffset,
        };
        sessionStorage.setItem(SESSION_KEY_CONTENT_EDITABLE_LAST_SELECTION, JSON.stringify(serializedRange));
      }
    },

    restoreSelection: () => {
      const { editable } = _helpers.getEditableNode();
      const savedRange = getStringToValidJSON(sessionStorage.getItem(SESSION_KEY_CONTENT_EDITABLE_LAST_SELECTION));
      if (!editable || !savedRange) return;

      const getNodeFromPath = (path, root = document.body) => {
        let node = root;
        for (let index of path) {
          if (!node || !node.childNodes[index]) return null;
          node = node.childNodes[index];
        }
        return node;
      };

      const { startPath, startOffset, endPath, endOffset } = savedRange;
      const startNode = getNodeFromPath(startPath);
      const endNode = getNodeFromPath(endPath);

      if (startNode && endNode) {
        const range = document.createRange();
        range.setStart(startNode, startOffset);
        range.setEnd(endNode, endOffset);

        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        editable.focus();
      }
    },

    generateMergeFieldNode: ({
      type,
      defaultModule,
      defaultField,
      defaultFieldData,
      cmfId,
      dataSource = "DocHipo",
    }) => {
      if (type === SYSTEM.SLUG) {
        // get document name from context (rather than depending on mfSchemaDataRef as this could hold old document name)
        const mergeKey = `${type}.${defaultModule?.slug?.trim()}.${defaultField?.slug?.trim()}`;
        if (mergeKey === "system.documents.document-name") {
          defaultFieldData = localStorage.getItem(LOCAL_KEY_DOCUMENT_NAME);
        }
      }

      if (type === CUSTOM.SLUG) {
        const targetWidgetIds = [];

        // update entire document with updated value for this custom mf
        const isEmpty = _helpers.isDataEmpty(defaultFieldData);
        const mergeKey = `${type}.${defaultField.slug}`;
        const allCustomMfs = _helpers.getUniqueMergeFields({ selector: `[data-mf-key="${mergeKey}"]` });

        allCustomMfs.forEach(cmf => {
          cmf.innerText = isEmpty ? defaultField.name : defaultFieldData;
          cmf.dataset.mfData = isEmpty ? "NULL" : defaultFieldData;
          cmf.classList.toggle("empty", isEmpty);
          cmf.classList.toggle("filled", !isEmpty);

          // store widget ids
          const widgetId = _helpers.getWidgetIdByMfNode({ mfNode: cmf });
          if (!targetWidgetIds.includes(widgetId)) targetWidgetIds.push(widgetId);
        });

        // store targetWidgetIds is session storage to update them from useTextFocusOut hook (while performing document save after text widget focus out)
        if (targetWidgetIds.length > 0) {
          sessionStorage.setItem(SESSION_KEY_TARGET_CMF_TEXT_WIDGET_IDS, JSON.stringify(targetWidgetIds));
        }
      }

      const randomId = `mf-${getRandomString(8)}`;
      const isEmpty = _helpers.isDataEmpty(defaultFieldData);

      const moduleName = defaultModule?.name?.trim();
      const moduleSlug = defaultModule?.slug?.trim();
      const fieldName = defaultField?.name?.trim();
      const fieldSlug = defaultField?.slug?.trim();

      const mergeLabel = moduleName ? `${moduleName}.${fieldName}` : fieldName;
      const mergeDataAttr = isEmpty ? "NULL" : defaultFieldData;
      const mergeDataUI = isEmpty ? mergeLabel : defaultFieldData;
      const highlightedClass = isEmpty ? "empty" : "filled";
      const mergeKey = moduleSlug ? `${type}.${moduleSlug}.${fieldSlug}` : `${type}.${fieldSlug}`;

      const htmlStr = removeWhiteSpaceBetweenTags(`
        <span 
          id="${randomId}"
          class="merge-field ${highlightedClass}"
          data-mf-type="${type}" 
          data-mf-source="${dataSource}"
          ${moduleSlug ? `data-mf-module="${moduleSlug}"` : ""}
          data-mf-field="${fieldSlug}"
          data-mf-label="${mergeLabel}" 
          data-mf-data="${mergeDataAttr}" 
          data-mf-key="${mergeKey}" 
          ${cmfId ? `data-mf-oid="${cmfId}"` : ""}
          >
          ${mergeDataUI}
        </span>
      `);
      const mergeFieldNode = parseStrToDomNode({ htmlStr, selector: "span" });
      return { mergeFieldNode };
    },

    insertRichTextAtHash: mergeField => {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      const { startContainer, startOffset } = range;

      if (startContainer.nodeType !== Node.TEXT_NODE) return;

      const textNode = startContainer;
      const text = textNode.nodeValue;

      // Ensure caret is just after #
      if (startOffset === 0 || text[startOffset - 1] !== "#") return;

      const beforeText = text.slice(0, startOffset - 1); // exclude #
      const afterText = text.slice(startOffset); // everything after #

      const charBeforeHash = text[startOffset - 2] || "";
      const charAfterHash = afterText[0] || "";

      const needsLeadingSpace = charBeforeHash && !/\s/.test(charBeforeHash);
      const needsTrailingSpace = charAfterHash && !/\s/.test(charAfterHash);

      const parent = textNode.parentNode;
      const fragment = document.createDocumentFragment();

      if (beforeText) fragment.appendChild(document.createTextNode(beforeText));
      if (needsLeadingSpace) fragment.appendChild(document.createTextNode("\u00A0"));
      fragment.appendChild(mergeField);
      if (needsTrailingSpace || afterText === "") {
        fragment.appendChild(document.createTextNode("\u00A0"));
      }
      if (afterText) fragment.appendChild(document.createTextNode(afterText));

      // Replace the text node with the new fragment
      parent.replaceChild(fragment, textNode);

      // Move caret after merge field or space
      const newRange = document.createRange();
      const caretNode = mergeField.nextSibling?.nodeValue === "\u00A0" ? mergeField.nextSibling : mergeField;
      newRange.setStartAfter(caretNode);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);

      // update handler after mf insertion
      _helpers.updateWidgetHandlerHeight();
    },

    updateWidgetHandlerHeight: () => {
      try {
        const handler = document.getElementById("dhp-widget-handler");
        const { editable, editableParent } = _helpers.getEditableNode();
        const {
          widget: {
            id: activeWidgetId,
            node: widget,
            data: widgetData,
            isChildWidget,
            parent: { id: groupId },
          },
        } = getWidgetAndParentsDomReference(metadata.activeWidgetId[0]);

        const target = editable ?? widget.querySelector(".primary-text-widget.dhp-widget-inner");
        const targetId = isChildWidget ? groupId : activeWidgetId;

        if (widgetData.assetType === TEXT) {
          const curSelection = document.querySelector(`[data-widget-id="${targetId}"].collborative-selector`);
          if (isChildWidget) {
            const { groupDivHeight } = getUpdatedGroupInnerWidgetPos(targetId, id, target.offsetHeight, dimension.zoom);

            // update DOM and group handler
            document.querySelector(`.dhp-content-editable-true-text .child-selected`).style.outline =
              "0px solid #00a4ef";
            document.querySelector(`.dhp-content-editable-true-text .child-selected .dhp-widget-inner`).style.outline =
              "2px solid #00a4ef";
            document.getElementById("dhp-widget-handler").style.height = `${groupDivHeight}px`;
            if (curSelection) curSelection.style.height = `${groupDivHeight}px`;
          } else {
            // handle rotated text widget
            const {
              translate: { x: handlerTransX, y: handlerTransY },
              rotate: { theta: handlerTheta },
            } = getCssTransformObj({
              transform: handler.style.transform,
            });
            const { left, top } = calculateNewPositionOnRotatedObjectResize(
              parseFloat(handlerTransX),
              parseFloat(handlerTransY),
              parseFloat(handler.style.width),
              parseFloat(target.offsetHeight),
              parseFloat(handler.style.width),
              parseFloat(handler.style.height),
              parseFloat(handlerTheta)
            );
            const handlerTransformStr = getCssTransformObj({
              translateX: `${left}px`,
              translateY: `${top}px`,
              transform: handler.style.transform,
              returnStr: true,
            });

            if (handler) {
              handler.style.transform = handlerTransformStr;
              handler.style.height = `${target.offsetHeight}px`;
            }

            if (editableParent) {
              editableParent.style.transform = handlerTransformStr;
              editableParent.style.height = `${target.offsetHeight}px`;
            } else {
              // fallback (update widget height via DOM) - fixed and will not execute this block
              widget.style.height = `${target.offsetHeight}px`;
            }

            if (curSelection) curSelection.style.height = `${target.offsetHeight}px`;
          }
        }
      } catch (_) {
        //
      }
    },

    formatDateTime: epochMs => {
      if (!userInfo?.time_zone) return;
      const dateFormat = {
        "DD-MM-YYYY": "ddd DD MMM",
        "MM-DD-YYYY": "ddd MMM DD",
      };

      const defaultFormats = {
        "date-time": `${dateFormat[userInfo?.date_format] || "MMM ddd DD"}, yyyy ${
          userInfo?.time_format?.substring(0, 2) == 24 ? "HH:mm" : "hh:mm A"
        }`,
      };

      const applicableFormat = defaultFormats["date-time"];

      const gmtValue = setGMTAtSession(userInfo?.time_zone);
      const resultDate = moment(epochMs * 1000)
        .utcOffset(gmtValue)
        .format(applicableFormat);

      return resultDate;
    },

    formatDateOnly: epochMs => {
      if (!userInfo?.time_zone) return;

      const dateFormat = {
        "DD-MM-YYYY": "ddd DD MMM, yyyy",
        "MM-DD-YYYY": "ddd MMM DD, yyyy",
      };

      const applicableFormat = dateFormat[userInfo?.date_format] || "MMM ddd DD, yyyy";

      const gmtValue = setGMTAtSession(userInfo?.time_zone);

      const resultDate = moment(epochMs * 1000)
        .utcOffset(gmtValue)
        .format(applicableFormat);

      return resultDate;
    },

    parseDateTime: ({ isoStr, dataType }) => {
      if (!isoStr) return isoStr;
      const _parseEngines = {
        date: _helpers.formatDateOnly,
        datetime: _helpers.formatDateTime,
      };
      const _pEngine = _parseEngines[dataType];
      if (!_pEngine) return isoStr;

      // convert ISO time string to epoch time (ms)
      const epochMs = Date.parse(isoStr) / 1000;

      // convert epoch seconds to human readable date-time
      const result = _pEngine(epochMs);

      if (epochMs === NaN || result === "Invalid date") return null;
      else return result;
    },

    isDataEmpty: data => {
      const isEmpty = ["", "NULL", null, undefined].includes(data);
      return isEmpty;
    },

    getSortedFields: ({ array }) => {
      // sort fields in alphabetical order (case-insensitive)
      const newArray = JSON.parse(JSON.stringify(array));
      const sorted = [...newArray].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
      return sorted;
    },

    isDarkColor: rgb => {
      return 0.2126 * rgb.match(/\d+/g)[0] + 0.7152 * rgb.match(/\d+/g)[1] + 0.0722 * rgb.match(/\d+/g)[2] < 128;
    },

    setHeighlightColor: () => {
      document.querySelectorAll(".dhp-page-block").forEach(block => {
        const blockBG =
          block.querySelector(".dhp-page-bgcolor-overlay")?.style?.backgroundColor || "rgb(255, 255, 255)";
        const isDarkBg = _helpers.isDarkColor(blockBG);
        const allMfs = [...block.querySelectorAll(".merge-field")];
        allMfs.forEach(mf => {
          mf.classList.toggle("dark", isDarkBg);
        });
      });
    },

    getFormattedSchemaData: async schemaData => {
      const formattedData = JSON.parse(JSON.stringify(schemaData));
      const { system: systemData = [], custom: customData = [], other: otherData = [] } = formattedData;

      // format sysyem schemaData
      formattedData[SYSTEM.SLUG] = DOCHIPO_SYSTEM.get_mfs(systemData);

      // format custom schemaData
      for (const field of customData) {
        field.isEmpty = _helpers.isDataEmpty(field.value); // set is empty props
      }

      // format other schemaData
      if ([TYPE_PROPOSAL].includes(documentType) && otherData) {
        const { data_source: { provider: dataSource = null } = {} } = documentDetails?.data;

        if (dataSource === "zoho_crm") {
          formattedData[OTHERS.SLUG] = await THIRD_PARTY_APPS.ZOHO_CRM.get_mfs(otherData);
        }
      }

      // console.log(formattedData);

      return formattedData;
    },

    postSchemaDataFetchActions: schemaData => {
      // sequence-1: update mfs with lates data (for first render in editor only) + then remove mf loader
      _helpers.updateAllMfsWithLatestData(schemaData);
      _helpers.mfLoader({ actionType: "remove" });

      // sequence-2: generate missing mfs (this is for first render in editor only)
      const { missingCmfPresent } = _helpers.generateMissingMfData();

      // sequence-3: show missing cmf update form if document creating from library templates and if any missing cmfs present (show first time only)
      if (isDocCreatingFromLibrary && missingCmfPresent) setShowMfBulkUpdateModal(true);
    },

    updateAllMfsWithLatestData: schemaData => {
      const { system: systemData = [], other: otherData = [] } = schemaData;

      const getLatestData = ({ mfType, mfModule, mfField, mfData }) => {
        if (mfType === SYSTEM.SLUG) {
          const moduleObj = systemData?.find(e => e?.slug === mfModule);
          const fieldObj = moduleObj?.merge_fields?.find(e => e?.slug === mfField);
          return fieldObj ?? { isEmpty: null, name: null, slug: null, value: null };
        }
        if (mfType === CUSTOM.SLUG) {
          return {
            isEmpty: isDocCreatingFromLibrary ? true : _helpers.isDataEmpty(mfData),
            name: null,
            slug: null,
            value: isDocCreatingFromLibrary ? null : mfData,
          };
        }
        if (mfType === OTHERS.SLUG) {
          if (![TYPE_PROPOSAL].includes(documentType)) return;
          if (otherData?.length > 0) {
            const moduleObj = otherData?.find(e => e?.slug === mfModule);
            const fieldObj = moduleObj?.merge_fields?.find(e => e?.slug === mfField);
            return fieldObj
              ? { ...fieldObj, moduleName: moduleObj.name }
              : { isEmpty: null, name: null, slug: null, value: null, moduleName: null };
          } else {
            // retain last saved data if third party integration is disabled
            return {
              isEmpty: _helpers.isDataEmpty(mfData),
              name: mfField,
              slug: mfField,
              value: mfData,
              moduleName: mfModule,
            };
          }
        }
      };

      const targetWidgetIds = [];
      const allMfs = _helpers.getUniqueMergeFields({ selector: ".merge-field" });

      allMfs.forEach(mf => {
        const {
          isEmpty,
          name: newFieldName,
          value: newFieldValue,
          moduleName: newModuleName,
        } = getLatestData({ ...mf.dataset });

        // for third party apps (Omfs)
        if (mf.dataset.mfType === OTHERS.SLUG && [TYPE_PROPOSAL].includes(documentType)) {
          const { data_source: { provider: dataSource = null } = {} } = documentDetails?.data;
          if (dataSource === "zoho_crm" && newModuleName && newFieldName) {
            mf.dataset.mfLabel = `${toProperCase(newModuleName)}.${newFieldName}`;
          }
        }

        mf.innerText = isEmpty ? mf.dataset.mfLabel : newFieldValue;
        mf.dataset.mfData = isEmpty ? "NULL" : newFieldValue;
        mf.classList.toggle("empty", isEmpty);
        mf.classList.toggle("filled", !isEmpty);

        // store widget ids
        const widgetId = _helpers.getWidgetIdByMfNode({ mfNode: mf });
        if (!targetWidgetIds.includes(widgetId)) targetWidgetIds.push(widgetId);
      });

      // update context to trigger document save
      _helpers.updateReactDom({ targetWidgetIds });
    },

    generateMissingMfData: () => {
      // Dynamically set merge fields color
      _helpers.setHeighlightColor();

      let systemMfs = [];
      let customMfs = [];
      let otherMfs = [];

      const allMfs = [...document.querySelectorAll(".merge-field")];
      allMfs.forEach(mf => {
        const { mfType, mfLabel, mfData, mfKey } = mf.dataset;
        const isEmpty = mfData === "NULL";
        const sysModule = isEmpty && mfType === SYSTEM.SLUG && mfLabel.split(".")?.[0];

        if (isEmpty && mfType === SYSTEM.SLUG) systemMfs.push({ key: mfKey, label: mfLabel, module: sysModule });
        if (isEmpty && mfType === CUSTOM.SLUG) customMfs.push({ key: mfKey, label: mfLabel });
        if (isEmpty && mfType === OTHERS.SLUG) otherMfs.push({ key: mfKey, label: mfLabel });
      });

      systemMfs = removeDuplicatesFromArrayOfObject({ arrayOfObj: systemMfs, keyToCompare: "key" });
      customMfs = removeDuplicatesFromArrayOfObject({ arrayOfObj: customMfs, keyToCompare: "key" });
      otherMfs = removeDuplicatesFromArrayOfObject({ arrayOfObj: otherMfs, keyToCompare: "key" });

      const missingMfData = {
        [SYSTEM.SLUG]: { mfs: systemMfs, total: systemMfs.length },
        [CUSTOM.SLUG]: { mfs: customMfs, total: customMfs.length },
        [OTHERS.SLUG]: { mfs: otherMfs, total: otherMfs.length },
        total: systemMfs.length + customMfs.length + otherMfs.length,
      };

      missingMfDataRef.current = missingMfData;
      return { missingCmfPresent: customMfs.length >= 1 };
    },

    getWidgetIdByMfNode: ({ mfNode }) => {
      const widget = mfNode.closest(".dhp-root-widget");
      return widget.id || widget.dataset.widgetId;
    },

    getUniqueMergeFields: ({ selector }) => {
      return [...new Map([...document.querySelectorAll(selector)].map(node => [node.id, node])).values()];
    },

    mfLoader: ({ actionType }) => {
      const allMfs = [...document.querySelectorAll(".merge-field")];
      allMfs.forEach(mf => mf?.classList?.[actionType]?.("dhp-mf-in-progress-loader"));
    },

    updateReactDom: ({ targetWidgetIds }) => {
      setTimeout(() => {
        updateWidgets(
          widgets.map(widget => {
            if (targetWidgetIds.includes(widget.id)) {
              const widgetNode = document.getElementById(widget.id);
              const widgetInner = widgetNode.querySelector(".dhp-widget-inner");
              const isGroupWidget = widgetNode.classList.contains("dhp-page-group");
              const widgetHeight = !isGroupWidget ? { height: widgetInner.offsetHeight } : {};

              return {
                ...widget,
                style: {
                  ...widget.style,
                  ...widgetHeight,
                },
                innerHTML: widgetNode.innerHTML,
              };
            } else {
              return widget;
            }
          })
        );
      }, 100);
    },
  };

  const DOCHIPO_SYSTEM = {
    module_fields_map: {
      ["documents"]: [
        { name: "Document Owner", slug: "document-owner", fields: ["document-owner"] },
        { name: "Document Name", slug: "document-name", fields: ["document-name"] },
        { name: "Description", slug: "description", fields: ["description"] },
        { name: "Created Time", slug: "created-time", fields: ["created-time"], special_field: true },
      ],

      ["document-owner"]: [
        { name: "First Name", slug: "firstname", fields: ["firstname"] },
        { name: "Middle Name", slug: "middlename", fields: ["middlename"] },
        { name: "Last Name", slug: "lastname", fields: ["lastname"] },
        { name: "Email", slug: "email", fields: ["email"] },
        { name: "Mobile", slug: "mobile", fields: ["mobile-isd-code", "mobile"], special_field: true },
        { name: "Phone", slug: "phone", fields: ["phone-isd-code", "phone", "phone-extension"], special_field: true },
        { name: "Address Line 1", slug: "street", fields: ["street"] },
        { name: "Address Line 2", slug: "addressline2", fields: ["addressline2"] },
        { name: "Country", slug: "country-code", fields: ["country-code"], special_field: true },
        { name: "State/Province", slug: "state", fields: ["state"] },
        { name: "City", slug: "city", fields: ["city"] },
        { name: "Zip/Postal Code", slug: "zip", fields: ["zip"] },
        { name: "Time Zone", slug: "time-zone", fields: ["time-zone"] },
        { name: "Date Format", slug: "date-format", fields: ["date-format"] },
        { name: "Time Format", slug: "time-format", fields: ["time-format"] },
      ],

      ["company"]: [
        { name: "Company Name", slug: "company-name", fields: ["company-name"] },
        { name: "Alias", slug: "alias", fields: ["alias"] },
        { name: "Phone", slug: "phone", fields: ["phone-isd-code", "phone", "phone-extension"], special_field: true },
        { name: "Fax", slug: "fax", fields: ["fax-isd-code", "fax"], special_field: true },
        { name: "Website", slug: "website", fields: ["website"] },
        { name: "Address Line 1", slug: "street", fields: ["street"] },
        { name: "Address Line 2", slug: "addressline2", fields: ["addressline2"] },
        { name: "Country", slug: "country-code", fields: ["country-code"], special_field: true },
        { name: "State/Province", slug: "state", fields: ["state"] },
        { name: "City", slug: "city", fields: ["city"] },
        { name: "Zip/Postal Code", slug: "zip", fields: ["zip"] },
        { name: "Time Zone", slug: "time-zone", fields: ["time-zone"] },
        { name: "Date Format", slug: "date-format", fields: ["date-format"] },
        { name: "Time Format", slug: "time-format", fields: ["time-format"] },
      ],
    },

    get_country_details_by_iso_code: ISO => {
      const countryObj = countries?.find(country => country.iso_code === ISO);
      return countryObj ?? { name: "", iso_code: "", isd_code: "", cluster_code: "" };
    },

    get_mfs: data => {
      const processedData = data.map(module => {
        const fieldObj = {};
        (module?.merge_fields || []).forEach(field => {
          fieldObj[field?.slug] = field?.value;
        });

        const fields = [];
        DOCHIPO_SYSTEM.module_fields_map?.[module?.slug]?.forEach(field => {
          let value = "";
          const { name: fieldName, slug: fieldSlug, fields: fieldGroup, special_field: isSpecialField } = field;
          const isGroupField = fieldGroup.length > 1;

          if (!isGroupField && !isSpecialField) {
            value = fieldObj?.[fieldSlug];
          }

          if (!isGroupField && isSpecialField) {
            if (fieldSlug === "created-time") {
              // convert epoch seconds to human readable date-time
              value = _helpers.formatDateTime(fieldObj?.[fieldSlug]);
            }
            if (fieldSlug === "country-code") {
              // get country name by iso code
              value = DOCHIPO_SYSTEM.get_country_details_by_iso_code(fieldObj?.[fieldSlug]).name;
            }
          }

          if (isGroupField && isSpecialField) {
            if (["fax", "phone", "mobile"].includes(fieldSlug)) {
              const ISO = fieldObj?.[fieldGroup?.[0]];
              const ISD = DOCHIPO_SYSTEM.get_country_details_by_iso_code(ISO).isd_code;
              const NUMBER = fieldObj?.[fieldGroup?.[1]];
              const EXTN = fieldObj?.[fieldGroup?.[2]];

              const ISD_CODE = !_helpers.isDataEmpty(ISD) ? `+${ISD} ` : "";
              const EXTENSION = !_helpers.isDataEmpty(EXTN) ? ` x ${EXTN}` : "";
              value = !_helpers.isDataEmpty(NUMBER) ? `${ISD_CODE}${NUMBER}${EXTENSION}` : "";
            }
          }

          fields.push({
            isEmpty: _helpers.isDataEmpty(value), // set is empty props
            name: fieldName,
            slug: fieldSlug,
            value,
          });
        });

        return {
          ...module,
          merge_fields: fields,
        };
      });

      return processedData;
    },
  };

  const THIRD_PARTY_APPS = {
    ZOHO_CRM: {
      excluded_fields: [
        "id",
        "Tag",
        "Currency",
        "Exchange_Rate",
        "Lead_Conversion_Time",
        "Sales_Cycle_Duration",
        "Overall_Sales_Duration",
      ],

      excluded_data_types: [
        "textarea",
        "fileupload",
        "imageupload",
        "profileimage",
        "subform",
        "territories",
        "rollup_summary",
      ],

      direct_mapping_data_types: [
        "text",
        "email",
        "integer",
        "bigint",
        "double",
        "boolean",
        "currency",
        "percent",
        "date",
        "datetime",
        "formula",
        "website",
        "phone",
        "picklist",
        "autonumber",
      ],

      get_mfs: async data => {
        const processedData = await Promise.all(
          Object.keys(data).map(async module => {
            const moduleName = toProperCase(module);
            const moduleSchema = await dispatch(fetchZohoCrmFieldsSchema({ moduleName }));

            const schemaObj = {};
            moduleSchema?.result?.forEach(field => {
              const { api_name, data_type, field_label } = field;
              schemaObj[api_name] = { data_type, field_label };
            });

            const fields = [];
            for (const [api_name, value] of Object.entries(data[module])) {
              const { data_type = null, field_label = null } = schemaObj?.[api_name] ?? {};
              if (
                !api_name.startsWith("$") &&
                !api_name.includes("__") &&
                !THIRD_PARTY_APPS.ZOHO_CRM.excluded_fields.includes(api_name) &&
                !THIRD_PARTY_APPS.ZOHO_CRM.excluded_data_types.includes(data_type)
              ) {
                let formattedVal;

                if (THIRD_PARTY_APPS.ZOHO_CRM.direct_mapping_data_types.includes(data_type)) {
                  if (["date", "datetime"].includes(data_type)) {
                    formattedVal = _helpers.parseDateTime({ isoStr: value, dataType: data_type });
                  } else formattedVal = value;
                } else if (["lookup", "userlookup", "ownerlookup"].includes(data_type)) {
                  formattedVal = value?.name;
                } else if (["multiselectpicklist"].includes(data_type)) {
                  formattedVal = value?.join(", ");
                } else if (["multiselectlookup"].includes(data_type)) {
                  formattedVal = value?.map(item => item?.[api_name]?.name)?.join(", ");
                }

                fields.push({
                  isEmpty: _helpers.isDataEmpty(formattedVal), // set is empty props
                  name: field_label,
                  slug: api_name,
                  value: formattedVal,
                });
              }
            }

            // sort fields in alphabetical order (case-insensitive)
            const sortedFields = _helpers.getSortedFields({ array: fields });

            return {
              name: getName(moduleName),
              slug: module.toLowerCase(),
              merge_fields: sortedFields,
            };
          })
        );

        return processedData;
      },
    },
  };

  useEffect(() => {
    if (!(process.env.IS_MERGE_FIELD_ENABLED && isDocumentReady) || isDocumentSigned) return;
    if (mfSchemaData && mfSchemaDataFirstFetch && !mfSchemaDataRef.current) {
      _helpers.mfLoader({ actionType: "add" });
      firstFetchPostActions();
    }
  }, [mfSchemaData, mfSchemaDataFirstFetch, isDocumentReady]);

  useEffect(() => {
    if (!(process.env.IS_MERGE_FIELD_ENABLED && isDocumentReady) || isDocumentSigned) return;
    if (changeBySocket) {
      firstFetchPostActions();
    }
  }, [changeBySocket, isDocumentReady]);

  useEffect(() => {
    if (!(process.env.IS_MERGE_FIELD_ENABLED && isDocumentReady) || isDocumentSigned) return;
    if (actionType === GET_CONNECTED_ACCOUNTS_SUCCESS) fetchSchemaData();
  }, [actionType, isDocumentReady]);

  useEffect(() => {
    if (!(process.env.IS_MERGE_FIELD_ENABLED && isDocumentReady) || isDocumentSigned) return;
    const isDocumentSaveCompleted = localStorage.getItem(LOCAL_KEY_DOCUMENT_SAVE_COMPLETED) === "true";
    if (isDocumentSaveCompleted) {
      localStorage.setItem(LOCAL_KEY_DOCUMENT_SAVE_COMPLETED, "false");
      documentSavePostActions();
    }
  }, [widgets, isDocumentReady]);

  useEffect(() => {
    if (!(process.env.IS_MERGE_FIELD_ENABLED && isDocumentReady)) return;
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("keydown", deleteMergeField);
    document.addEventListener("selectionchange", handleSelectionChange);
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mouseup", expandSelectionToMergeFields);
    document.addEventListener("dragstart", restrictDraggingMergeFields);
    document.addEventListener("drop", restrictDropInsideMergeField);
    document.getElementById("canvas-panel-area")?.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("keydown", deleteMergeField);
      document.removeEventListener("selectionchange", handleSelectionChange);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", expandSelectionToMergeFields);
      document.removeEventListener("dragstart", restrictDraggingMergeFields);
      document.removeEventListener("drop", restrictDropInsideMergeField);
      document.getElementById("canvas-panel-area")?.removeEventListener("scroll", handleScroll);
    };
  }, [isDocumentReady]);

  return {
    insertMergeField,
    deleteMergeFieldFromInfoModal,
    updateMergeFieldFromInfoModal,
    bulkUpdateCustomMergeFields,
    fetchSchemaData,
    schemaFetching,
  };
};

export default useMergeFields;
